import { Dtos } from "@inrev/common";
import {
	useAdminCreateEntityTag,
	useAdminFetchAllEntityTags,
	useAdminUpdateEntityTag,
} from "@portal/api/entity/admin";
import { useCallback, useMemo, useState } from "react";
import { HiOutlinePlus, HiOutlineTag } from "react-icons/hi2";
import { Input } from "../form/inputs/Input";
import { Modal } from "../layout/Modal";
import { Button } from "../ui/Button";
import { ErrorDisplay } from "../ui/ErrorDisplay";
import { MutationErrorFlag } from "../ui/MutationErrorFlag";
import { SearchInput } from "../ui/SearchInput";
import { Spinner } from "../ui/Spinner";
import { Tag } from "../ui/Tag";

export enum TagErrorMessage {
	InvalidCharacters = "Tag name can only contain alphanumeric characters, spaces, and - & + . , ' ( ) : /",
	InvalidLength = "Tag name must be between 1 and 30 characters long",
	NotUnique = "Tag name must be unique",
}

export const ManageEntityTags = () => {
	const [editTag, setEditTag] = useState<Dtos.Tag.Get.Response | null>(null);
	const [showCreate, setShowCreate] = useState(false);
	const [inputTagName, setInputTagName] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const { entityTags, entityTagsLoading, entityTagsError, refetchEntityTags } =
		useAdminFetchAllEntityTags();
	const { createEntityTag, createEntityTagError, dismissCreateEntityTagError } =
		useAdminCreateEntityTag();
	const { updateEntityTag, updateEntityTagError, dismissUpdateEntityTagError } =
		useAdminUpdateEntityTag();

	const filteredTags = useMemo(() => {
		if (!searchTerm) {
			return entityTags;
		}

		return entityTags.filter((tag) =>
			tag.displayName.toLowerCase().includes(searchTerm.toLowerCase()),
		);
	}, [entityTags, searchTerm]);

	const validateTagName = useCallback(() => {
		const trimmedLowerCaseName = inputTagName.toLowerCase().trim();
		const tagNameRegex = /^[a-zA-Z0-9-_&+.,'():\/ ]+$/; // Only allow case-insensitive (for create) alphanumeric characters, spaces, and - & + . , ' ( ) : /

		// Only allow names that match the regex, are between 1 and 30 characters, and are unique
		if (!tagNameRegex.test(trimmedLowerCaseName)) {
			return {
				isValid: false,
				errorMessage: TagErrorMessage.InvalidCharacters,
			};
		} else if (trimmedLowerCaseName.length === 0 || trimmedLowerCaseName.length > 30) {
			return {
				isValid: false,
				errorMessage: TagErrorMessage.InvalidLength,
			};
		} else if (
			entityTags.some(
				({ id, displayName }) =>
					displayName.toLowerCase() === trimmedLowerCaseName && id !== editTag?.id, // Allow the same name only if editing the same tag
			)
		) {
			return {
				isValid: false,
				errorMessage: TagErrorMessage.NotUnique,
			};
		}

		return { isValid: true, errorMessage: "" };
	}, [entityTags, inputTagName, editTag]);

	const handleClose = useCallback(() => {
		setEditTag(null);
		setShowCreate(false);
		setInputTagName("");
		setErrorMessage(undefined);
	}, [setEditTag, setShowCreate, setInputTagName, setErrorMessage]);

	const handleModalSubmit = useCallback(() => {
		const { isValid, errorMessage } = validateTagName();
		if (isValid) {
			// Update
			if (editTag) {
				updateEntityTag({
					tagId: editTag.id,
					updatedDisplayName: { displayName: inputTagName.trim() },
				});
			} else {
				// Create
				createEntityTag({ displayName: inputTagName.trim() });
			}

			handleClose();
		} else {
			setErrorMessage(errorMessage);
		}
	}, [editTag, validateTagName, handleClose]);

	return (
		<>
			<div className="flex items-center justify-between mb-4">
				<div className="flex items-center pr-3 py-2">
					<HiOutlineTag size={20} />
					<span className="ml-2 text-xl font-medium">Entity Tags</span>
				</div>
				<div className="flex items-center ml-4 border rounded-lg text-secondary text-sm w-[200px]">
					<SearchInput
						name="entity-tag-search"
						placeholder="Search tags..."
						onChange={(value) => {
							setSearchTerm(value);
						}}
						className="flex-grow bg-transparent"
						hideFocusStyles
					/>
				</div>
			</div>
			<div
				data-testid="entity-tags-container"
				className="flex flex-col justify-center items-start w-full border rounded-lg"
			>
				{entityTagsLoading && (
					<div
						data-testid="entity-tags-loading"
						className="flex p-4 w-full justify-center items-center"
					>
						<Spinner />
					</div>
				)}
				{entityTagsError && (
					<ErrorDisplay
						error={entityTagsError}
						componentToDisplay={
							<div
								data-testid="entity-tags-error"
								className="flex w-full p-4 text-sm justify-center items-center"
							>
								<span>Unable to load tags.</span>
								<a
									data-testid="retry-link"
									className="cursor-pointer m-1 text-link hover:text-link-hover"
									onClick={() => refetchEntityTags()}
								>
									Retry
								</a>
							</div>
						}
					/>
				)}
				{entityTags && !entityTagsLoading && !entityTagsError && (
					<div className="flex flex-wrap gap-2 p-4">
						{filteredTags.length === 0 ? (
							<div
								data-testid="empty-entity-tags"
								className="flex w-full p-4 text-sm justify-center items-center"
							>
								<span>No entity tags.</span>
							</div>
						) : (
							filteredTags.map((tag) => (
								<Tag
									key={tag.id}
									onClick={() => {
										setEditTag(tag);
										setInputTagName(tag.displayName);
									}}
								>
									{tag.displayName}
								</Tag>
							))
						)}
					</div>
				)}
			</div>
			<Button
				data-testid="add-tags-button"
				className="mt-4"
				color="primary"
				filled
				disabled={entityTagsLoading || Boolean(entityTagsError)}
				onClick={() => {
					setShowCreate(true);
					setInputTagName("");
				}}
			>
				<HiOutlinePlus className="inline-block mr-1 text-[16px] text-inverse stroke-[1.5]" />
				Add Tag
			</Button>
			{(editTag || showCreate) && (
				<Modal onClickOutside={handleClose}>
					<div
						data-testid="entity-tag-modal"
						className="flex flex-col p-6 bg-white rounded-md shadow-lg"
					>
						<h2 className="text-lg font-medium mb-2">
							{editTag ? "Update Entity Tag" : "New Entity Tag"}
						</h2>
						{errorMessage && (
							<div className="flex w-[200px] text-wrap mb-2">
								<div className="text-xs text-red-500">{errorMessage}</div>
							</div>
						)}
						<Input
							name="tag-name"
							placeholder="Tag name"
							containerClassName="mb-4"
							value={inputTagName}
							onChange={(e) => setInputTagName(e.target.value)}
							error={!!errorMessage}
						/>
						<Button
							onClick={handleModalSubmit}
							color="primary"
							filled
							disabled={!inputTagName.trim() || editTag?.displayName === inputTagName.trim()}
						>
							{editTag ? "Update" : "Create"}
						</Button>
					</div>
				</Modal>
			)}
			<MutationErrorFlag
				errorHeading="Create Tag Error"
				mutationError={createEntityTagError}
				dismissError={dismissCreateEntityTagError}
			/>
			<MutationErrorFlag
				errorHeading="Update Tag Error"
				mutationError={updateEntityTagError}
				dismissError={dismissUpdateEntityTagError}
			/>
		</>
	);
};
